/**
 * Barra di filtri delle statistiche della scheda: tre tendine con il nome
 * dentro (`Filtro`, §1.14), il periodo in un calendario e «Azzera» quando
 * qualcosa e filtrato. Stanno in una griglia che decide sulla larghezza della
 * card, non della vista: una colonna quando e stretta, due quando c'e spazio.
 * I filtri stanno nella query string.
 */

import { CalendarIcon, XIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { it } from 'react-day-picker/locale';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { giornoUtc } from '../comuni/date.js';
import { dataBreve } from '../comuni/dati.js';
import { Filtro, TUTTI } from '../comuni/FiltriVista.js';
import {
  type FiltriDisponibili,
  type FiltriScheda,
  filtriDaSearch,
  percorsoConFiltri,
} from './elenchi.js';

/**
 * Il calendario lavora in UTC, come `formatoGiorno`: un giorno e la sua
 * mezzanotte UTC, e nessun fuso del dispositivo lo sposta.
 */
function giorno(data: string | undefined): Date | undefined {
  return data ? giornoUtc(data) : undefined;
}

function data(d: Date | undefined): string | undefined {
  return d?.toISOString().slice(0, 10);
}

function etichettaPeriodo(da?: string, a?: string): string {
  if (da && a) return `${dataBreve(da)} – ${dataBreve(a)}`;
  if (da) return `Dal ${dataBreve(da)}`;
  if (a) return `Fino al ${dataBreve(a)}`;
  return 'Periodo';
}

export function FiltriScheda({ disponibili }: { disponibili: FiltriDisponibili }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const filtri = filtriDaSearch(search);
  const attivi = Object.values(filtri).some(Boolean);

  const imposta = (cambi: Partial<FiltriScheda>) => {
    const prossimo: FiltriScheda = { ...filtri, ...cambi };
    for (const k of Object.keys(prossimo) as (keyof FiltriScheda)[]) {
      if (!prossimo[k]) delete prossimo[k];
    }
    navigate(percorsoConFiltri(pathname, prossimo), { replace: true });
  };

  const tendina = (
    campo: 'stagione' | 'competizione' | 'squadraStagione',
    etichetta: string,
    voci: { id: string; nome: string }[],
  ) => (
    <Filtro
      className="@xl:max-w-none"
      etichetta={etichetta}
      valore={voci.some((v) => v.id === filtri[campo]) ? filtri[campo]! : TUTTI}
      onCambia={(v) => imposta({ [campo]: v === TUTTI ? '' : v })}
      opzioni={[
        { valore: TUTTI, etichetta: 'Tutte' },
        ...voci.map((v) => ({ valore: v.id, etichetta: v.nome })),
      ]}
    />
  );
  const periodo = Boolean(filtri.da || filtri.a);

  return (
    <div className="@container">
      <div className="grid grid-cols-1 gap-2 @sm:grid-cols-2">
        {tendina('stagione', 'Stagione', disponibili.stagioni)}
        {tendina('competizione', 'Competizione', disponibili.competizioni)}
        {tendina(
          'squadraStagione',
          'Squadra',
          disponibili.squadreStagione.map((s) => ({
            id: s.id,
            nome: `${s.squadra} · ${s.stagione}`,
          })),
        )}
        {/* Il pulsante del periodo ha bordo e fondo delle tendine accanto, non dei
          pulsanti: in alcuni temi `--input` e `--border` sono colori diversi. */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'w-full justify-start border-input bg-transparent font-normal dark:bg-input/30',
                !periodo && 'text-muted-foreground',
              )}
            >
              <CalendarIcon data-icon="inline-start" />
              {etichettaPeriodo(filtri.da, filtri.a)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center">
            <Calendar
              mode="range"
              locale={it}
              timeZone="UTC"
              numberOfMonths={1}
              defaultMonth={giorno(filtri.da) ?? giorno(filtri.a)}
              selected={{ from: giorno(filtri.da), to: giorno(filtri.a) }}
              onSelect={(r: DateRange | undefined) =>
                imposta({ da: data(r?.from), a: data(r?.to) })
              }
            />
          </PopoverContent>
        </Popover>
        {attivi && (
          <Button
            variant="ghost"
            size="sm"
            className="col-span-full justify-self-center"
            onClick={() =>
              imposta({ stagione: '', competizione: '', squadraStagione: '', da: '', a: '' })
            }
          >
            <XIcon data-icon="inline-start" />
            Azzera
          </Button>
        )}
      </div>
    </div>
  );
}
